import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createODA, fetchBlotter, selectors, updateOda } from 'state';
import { priceFormat } from 'services/formatters';

export type ODALeg = 'stopLoss' | 'takeProfit';
export type ODAField = 'price' | 'percentage';

type Draft = {
  price: number | null;
  field: ODAField;
  text: string;
  entry: number;
};

const readNumber = (text: string) => {
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(text.trim())) {
    return null;
  }
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
};

const isPositive = (value: number) => Number.isFinite(value) && value > 0;

const priceFromPercentage = (
  entry: number,
  percentage: number,
  sign: number,
) => {
  if (percentage === 0) return entry;
  // Remove floating-point noise only from calculated prices. Saved and typed
  // prices never pass through this rounding or through the displayed percent.
  return Number((entry + (sign * entry * percentage) / 100).toPrecision(15));
};

const useODAForm = (
  id: number,
  onComplete: () => void,
  tradingBlocked = false,
) => {
  const dispatch = useDispatch();
  const { fetched_blotter, fetching_blotter } = useSelector(selectors.player);
  const instrument = useSelector(selectors.financialInstrumentById(id));
  const asset = useSelector(selectors.underlyingAssetOfInstrument(instrument));
  const currency = useSelector(selectors.currencyById(asset.base_currency));
  const oda = useSelector(selectors.odaOfInstrumentCreateSelector(id));
  const formatPrice = (price: number) =>
    priceFormat(price, asset.ticker, currency?.symbol, asset.resourcetype);
  const entry = instrument.avg_open_price;
  const direction = Math.sign(instrument.amount);
  const quoteName = direction > 0 ? 'bid' : 'ask';
  const currentPrice = direction > 0 ? asset.bid : asset.ask;
  const loading = oda !== undefined && (!oda.stopLoss || !oda.takeProfit);

  const key = `${id}:${direction}`;
  const [drafts, setDrafts] = useState<
    { key: string } & Partial<Record<ODALeg, Draft>>
  >({ key });
  // A different instrument or position side starts a different ticket.
  if (drafts.key !== key) setDrafts({ key });

  useEffect(() => {
    if (!fetching_blotter && !fetched_blotter) dispatch(fetchBlotter({}));
  }, [dispatch, fetching_blotter, fetched_blotter]);

  const referenceError =
    !Number.isFinite(instrument.amount) || direction === 0
      ? 'An open position is required to set an ODA.'
      : !isPositive(entry)
        ? 'A valid open price is required to calculate SL and TP percentages.'
        : !isPositive(currentPrice)
          ? `A valid current ${quoteName} is required to set an ODA.`
          : undefined;

  const getSign = (leg: ODALeg) => direction * (leg === 'takeProfit' ? 1 : -1);
  const getLeg = (leg: ODALeg) => {
    const sign = getSign(leg);
    const draft = drafts.key === key ? drafts[leg] : undefined;
    const savedPrice = oda?.[leg]?.limit_price;
    const quoteBeyondEntry = sign * (currentPrice - entry) > 0;
    const defaultPrice = referenceError
      ? null
      : priceFromPercentage(
          quoteBeyondEntry ? currentPrice : entry,
          quoteBeyondEntry ? 1 : 2,
          sign,
        );
    const price = draft
      ? draft.price
      : oda
        ? (savedPrice ?? null)
        : defaultPrice;
    const percentage =
      price !== null && isPositive(entry)
        ? (sign * (price - entry) * 100) / entry
        : null;
    const percentageDraft =
      draft?.field === 'percentage' && draft.entry === entry;
    const label = leg === 'stopLoss' ? 'Stop loss' : 'Take profit';

    let error: string | undefined;
    if (!referenceError) {
      if (percentageDraft && readNumber(draft.text) < 0) {
        error = 'Percentage cannot be negative.';
      } else if (price === null || !Number.isFinite(price)) {
        error = 'Enter a valid price or percentage.';
      } else if (price <= 0) {
        error = 'Price must be greater than zero.';
      } else if (!Number.isFinite(percentage)) {
        error = 'Enter a valid price or percentage.';
      } else if (percentage < 0) {
        error = `${label} must be at or ${sign > 0 ? 'above' : 'below'} open price (${formatPrice(entry)}). Use 0% or more.`;
      } else if (sign * (price - currentPrice) <= 0) {
        error = `${label} must be ${sign > 0 ? 'above' : 'below'} the current ${quoteName} (${formatPrice(currentPrice)}).`;
      }
    }

    return {
      price,
      percentageNegative: percentage !== null && percentage < 0,
      priceInput: draft?.field === 'price' ? draft.text : String(price ?? ''),
      percentageInput: percentageDraft
        ? draft.text
        : percentage !== null && Number.isFinite(percentage)
          ? String(Number(percentage.toPrecision(12)))
          : '',
      error,
    };
  };

  const stopLoss = getLeg('stopLoss');
  const takeProfit = getLeg('takeProfit');
  const disabled = Boolean(
    tradingBlocked ||
      loading ||
      referenceError ||
      stopLoss.error ||
      takeProfit.error,
  );

  const change = (leg: ODALeg, field: ODAField, text: string) => {
    const value = readNumber(text);
    const price =
      field === 'price'
        ? value
        : value !== null && isPositive(entry)
          ? priceFromPercentage(entry, value, getSign(leg))
          : null;
    setDrafts(previous => ({
      ...(previous.key === key ? previous : { key }),
      [leg]: { price, field, text, entry },
    }));
  };

  const submit = () => {
    if (disabled) return;
    dispatch(
      (oda ? updateOda : createODA)({
        financial_instrument: instrument.id,
        stop_loss_price: stopLoss.price,
        take_profit_price: takeProfit.price,
      }),
    );
    onComplete();
  };

  return {
    ticketKey: key,
    name: asset.name,
    entry,
    currentPrice,
    formatPrice,
    quoteName,
    loading,
    referenceError,
    percentageDisabled: !isPositive(entry) || direction === 0,
    stopLoss,
    takeProfit,
    change,
    disabled,
    submit,
    actionLabel: oda ? 'Update ODA' : 'Set ODA',
  };
};

export default useODAForm;
